import { Controller } from "@hotwired/stimulus"

// Client-side filter controller for the bill items table (inpatient itemized review).
// Reads filter config from a static definition, manages draft/apply pattern,
// renders chips + dropdown panels, and shows/hides table rows based on data attributes.
export default class extends Controller {
  static targets = [
    "toggleButton", "buttonLabel", "noFiltersText",
    "chipsContainer", "overflowButton", "clearAllButton",
    "dropdown", "categoryList", "rightPanel", "draftCount",
    "tableBody"
  ]

  static values = {
    activeFilters: { type: Object, default: {} }
  }

  connect() {
    this.MAX_VISIBLE_CHIPS = 3
    this.draftFilters = {}
    this.selectedCategory = null
    this.isOpen = false

    this.filterConfig = [
      {
        key: "has_system_flag",
        label: "Has System Flag",
        type: "boolean_select",
        options: [
          { value: "true", label: "True", description: "Items flagged by the system" },
          { value: "false", label: "False", description: "Items without system flags" }
        ]
      },
      {
        key: "human_review",
        label: "Human Review",
        type: "enum_select",
        options: [
          { value: "verified", label: "Verified", description: "Items marked as verified by a human reviewer" },
          { value: "flagged", label: "Flagged", description: "Items flagged by a human reviewer" }
        ]
      },
      {
        key: "has_been_reviewed",
        label: "Has Been Human Reviewed",
        type: "boolean_select",
        options: [
          { value: "true", label: "True", description: "Items that have been reviewed by a human" },
          { value: "false", label: "False", description: "Items not yet reviewed by a human" }
        ]
      },
      {
        key: "revenue_code",
        label: "Revenue Code",
        type: "text_search",
        placeholder: "Search revenue codes..."
      },
      {
        key: "hcpcs_code",
        label: "HCPCS Code",
        type: "text_search",
        placeholder: "Search HCPCS codes..."
      },
      {
        key: "service_date",
        label: "Date",
        type: "date_range"
      },
      {
        key: "charges",
        label: "Charges Amount",
        type: "currency_range"
      }
    ]

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("mousedown", this.handleClickOutside)
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)

    this.renderChips()
    this.updateButtonLabel()
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  // --- Dropdown open/close ---

  toggleDropdown(event) {
    event?.stopPropagation()
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown(initialCategory = null) {
    this.draftFilters = JSON.parse(JSON.stringify(this.activeFiltersValue))
    this.selectedCategory = initialCategory || this.filterConfig[0]?.key || null
    this.isOpen = true
    this.dropdownTarget.classList.remove("hidden")
    this.renderCategoryList()
    this.renderRightPanel()
    this.updateDraftCount()
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
  }

  cancel() { this.closeDropdown() }

  handleClickOutside(event) {
    if (!this.isOpen) return
    if (this.element.contains(event.target)) return
    this.closeDropdown()
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    if (event.key === "Escape") this.closeDropdown()
  }

  onDropdownClick(event) { event.stopPropagation() }

  // --- Apply / Clear ---

  apply() {
    this.activeFiltersValue = JSON.parse(JSON.stringify(this.draftFilters))
    this.closeDropdown()
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  clearAll() {
    this.activeFiltersValue = {}
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  removeChip(key, value) {
    const filters = JSON.parse(JSON.stringify(this.activeFiltersValue))
    if (filters[key]) {
      filters[key] = filters[key].filter(v => v !== value)
      if (filters[key].length === 0) delete filters[key]
    }
    this.activeFiltersValue = filters
    this.renderChips()
    this.updateButtonLabel()
    this.applyFiltersToRows()
  }

  // --- Row filtering ---

  applyFiltersToRows() {
    const filters = this.activeFiltersValue
    const hasFilters = Object.keys(filters).length > 0
    const tbody = this.tableBodyTarget
    const rows = tbody.querySelectorAll("tr[data-filter-item-id]")

    // Track which parent rows have visible children
    const parentVisibility = {}

    rows.forEach(row => {
      const isParent = row.dataset.filterIsParent === "true"
      const isChild = row.dataset.filterIsChild === "true"
      const parentRowId = row.dataset.parentRowId

      if (isParent) {
        // Parents are handled after children
        return
      }

      if (!hasFilters) {
        // No filters — respect the expandable-table-rows hidden state only
        // Don't touch the hidden class from the expandable-table-rows controller
        row.dataset.filterHidden = "false"
        if (parentRowId) {
          parentVisibility[parentRowId] = true
        }
        return
      }

      const visible = this.rowMatchesFilters(row, filters)
      row.dataset.filterHidden = visible ? "false" : "true"

      if (visible && parentRowId) {
        parentVisibility[parentRowId] = true
      }
    })

    // Now handle parent rows
    rows.forEach(row => {
      if (row.dataset.filterIsParent !== "true") return

      if (!hasFilters) {
        row.dataset.filterHidden = "false"
        return
      }

      const rowId = row.dataset.filterItemId
      // A parent is visible if any of its children are visible
      row.dataset.filterHidden = parentVisibility[rowId] ? "false" : "true"
    })

    // Apply visibility via CSS: add/remove a class rather than toggling hidden
    // so it doesn't conflict with expandable-table-rows
    this.updateRowVisibility()
  }

  updateRowVisibility() {
    const tbody = this.tableBodyTarget
    const rows = tbody.querySelectorAll("tr[data-filter-item-id]")
    rows.forEach(row => {
      if (row.dataset.filterHidden === "true") {
        row.classList.add("filter-hidden")
      } else {
        row.classList.remove("filter-hidden")
      }
    })
  }

  rowMatchesFilters(row, filters) {
    for (const [key, values] of Object.entries(filters)) {
      if (!values || values.length === 0) continue

      const config = this.filterConfig.find(f => f.key === key)
      if (!config) continue

      if (config.type === "boolean_select") {
        const dataVal = row.dataset[`filter${this.camelize(key)}`]
        if (!values.includes(dataVal)) return false
      } else if (config.type === "enum_select") {
        const dataVal = row.dataset[`filter${this.camelize(key)}`]
        if (!values.includes(dataVal)) return false
      } else if (config.type === "text_search") {
        const dataVal = (row.dataset[`filter${this.camelize(key)}`] || "").toLowerCase()
        if (!dataVal) return false
        const matches = values.some(v => dataVal === v.toLowerCase())
        if (!matches) return false
      } else if (config.type === "date_range") {
        const dataVal = row.dataset[`filter${this.camelize(key)}`]
        if (!dataVal) return false
        const [from, to] = (values[0] || "").split("|")
        if (from && dataVal < from) return false
        if (to && dataVal > to) return false
      } else if (config.type === "currency_range") {
        const dataVal = parseFloat(row.dataset[`filter${this.camelize(key)}`])
        if (isNaN(dataVal)) return false
        const [exact, min, max] = (values[0] || "").split("|")
        if (exact && dataVal !== parseFloat(exact)) return false
        if (min && dataVal < parseFloat(min)) return false
        if (max && dataVal > parseFloat(max)) return false
      }
    }
    return true
  }

  camelize(str) {
    return str.replace(/(^|_)([a-z])/g, (_, __, c) => c.toUpperCase())
  }

  // --- Rendering ---

  updateButtonLabel() {
    const count = this.getAllChips().length
    this.buttonLabelTarget.textContent = count > 0 ? `Filters (${count})` : "Add filters"
  }

  getAllChips() {
    const chips = []
    const filters = this.activeFiltersValue
    for (const [key, values] of Object.entries(filters)) {
      const config = this.filterConfig.find(f => f.key === key)
      if (!config) continue
      for (const val of values) {
        let label
        if (config.type === "enum_select" || config.type === "boolean_select") {
          const opt = (config.options || []).find(o => o.value === val)
          label = opt ? opt.label : val
          if (config.type === "boolean_select") label = `${config.label}: ${label}`
        } else if (config.type === "text_search") {
          label = `${config.label}: ${val}`
        } else if (config.type === "date_range") {
          label = `${config.label}: ${this.formatDateRange(val)}`
        } else if (config.type === "currency_range") {
          label = `${config.label}: ${this.formatCurrencyRange(val)}`
        } else {
          label = val
        }
        chips.push({ key, value: val, label })
      }
    }
    return chips
  }

  renderChips() {
    const allChips = this.getAllChips()
    const visibleChips = allChips.slice(0, this.MAX_VISIBLE_CHIPS)
    const overflowCount = allChips.length - this.MAX_VISIBLE_CHIPS
    const hasFilters = allChips.length > 0

    this.chipsContainerTarget.innerHTML = ""
    for (const chip of visibleChips) {
      this.chipsContainerTarget.appendChild(this.createChipElement(chip))
    }

    this.noFiltersTextTarget.classList.toggle("hidden", hasFilters)

    if (overflowCount > 0) {
      this.overflowButtonTarget.textContent = `+${overflowCount} more`
      this.overflowButtonTarget.classList.remove("hidden")
    } else {
      this.overflowButtonTarget.classList.add("hidden")
    }

    this.clearAllButtonTarget.classList.toggle("hidden", !hasFilters)
  }

  createChipElement(chip) {
    const el = document.createElement("div")
    el.className = "px-4 py-2 rounded-lg justify-end items-center gap-4 inline-flex border bg-support-light-250 text-support-dark-400 border-support-light-350 hover:bg-support-light-300 hover:text-support-dark-500 hover:border-support-light-400 whitespace-nowrap"

    const labelSpan = document.createElement("div")
    labelSpan.className = "text-xs font-semibold leading-[18px]"
    labelSpan.textContent = chip.label
    el.appendChild(labelSpan)

    const btn = document.createElement("div")
    btn.className = "cursor-pointer"
    btn.setAttribute("role", "button")
    btn.setAttribute("aria-label", `Remove ${chip.label}`)
    btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 32 32" fill="none" class="flex-shrink-0"><path d="M25 7L7 25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M25 25L7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.removeChip(chip.key, chip.value)
    })
    el.appendChild(btn)

    return el
  }

  // --- Category list ---

  renderCategoryList() {
    const container = this.categoryListTarget
    const header = container.querySelector("div")
    container.innerHTML = ""
    container.appendChild(header)

    for (const config of this.filterConfig) {
      const btn = document.createElement("button")
      const isActive = this.selectedCategory === config.key
      const count = (this.draftFilters[config.key] || []).length

      btn.className = `flex items-center gap-2 text-left cursor-pointer rounded-md transition-all duration-100 py-[7px] px-[10px] text-[13px] border ${
        isActive
          ? "bg-core-purple-200 border-core-purple-300 font-semibold text-core-purple-500"
          : "bg-transparent border-transparent font-medium text-support-dark-700 hover:bg-support-light-250 hover:border-support-light-350"
      }`
      btn.style.width = "calc(100% - 16px)"
      btn.style.margin = "0 8px"

      const hasSelections = count > 0
      btn.innerHTML = `
        ${hasSelections ? this.checkCircleIcon() : this.categoryIcon(config.type)}
        <span class="overflow-hidden text-ellipsis whitespace-nowrap flex-1">${config.label}</span>
      `

      btn.addEventListener("click", () => {
        this.selectedCategory = config.key
        this.renderCategoryList()
        this.renderRightPanel()
      })

      container.appendChild(btn)
    }
  }

  checkCircleIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" class="flex-shrink-0 fill-core-purple-500"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"/></svg>`
  }

  categoryIcon(type) {
    const icons = {
      boolean_select: `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="8" r="3" fill="currentColor"/></svg>`,
      enum_select: `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45"><rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 3.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="1" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 11.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
      text_search: `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45"><circle cx="6.5" cy="6.5" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M10 10l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
      date_range: `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45"><rect x="1.5" y="2.5" width="13" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M1.5 6.5h13" stroke="currentColor" stroke-width="1.5"/><path d="M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
      currency_range: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M152,120H136V56h8a32,32,0,0,1,32,32,8,8,0,0,0,16,0,48.05,48.05,0,0,0-48-48h-8V24a8,8,0,0,0-16,0V40H112a48,48,0,0,0,0,96h8v64H104a32,32,0,0,1-32-32,8,8,0,0,0-16,0,48.05,48.05,0,0,0,48,48h16v16a8,8,0,0,0,16,0V216h16a48,48,0,0,0,0-96Zm-32,0H112a32,32,0,0,1,0-64h8Zm32,80H136V136h16a32,32,0,0,1,0,64Z"/></svg>`
    }
    return icons[type] || icons.text_search
  }

  // --- Right panel rendering ---

  renderRightPanel() {
    const config = this.filterConfig.find(f => f.key === this.selectedCategory)
    if (!config) return

    const panel = this.rightPanelTarget
    panel.innerHTML = ""

    if (config.type === "boolean_select") {
      this.renderBooleanSelectPanel(panel, config)
    } else if (config.type === "enum_select") {
      this.renderEnumSelectPanel(panel, config)
    } else if (config.type === "text_search") {
      this.renderTextSearchPanel(panel, config)
    } else if (config.type === "date_range") {
      this.renderDateRangePanel(panel, config)
    } else if (config.type === "currency_range") {
      this.renderCurrencyRangePanel(panel, config)
    }
  }

  // --- Boolean Select Panel ---

  renderBooleanSelectPanel(container, config) {
    const selected = this.draftFilters[config.key] || []
    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 overflow-y-auto p-4 flex flex-col gap-0.5"

    const updateItems = () => {
      const currentSelected = this.draftFilters[config.key] || []
      for (const item of wrapper.children) {
        const optValue = item.dataset.optValue
        const isChecked = currentSelected.includes(optValue)
        item.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
          isChecked ? "bg-core-purple-200" : "bg-transparent hover:bg-support-light-250"
        }`
        const radio = item.querySelector("[data-radio]")
        if (radio) {
          radio.className = `w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
            isChecked ? "border-core-purple-500 bg-core-purple-500" : "border-support-light-400 bg-white"
          }`
          radio.innerHTML = isChecked ? `<div class="w-[8px] h-[8px] rounded-full bg-white"></div>` : ""
        }
      }
    }

    for (const opt of (config.options || [])) {
      const isChecked = selected.includes(opt.value)
      const label = document.createElement("label")
      label.dataset.optValue = opt.value
      label.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
        isChecked ? "bg-core-purple-200" : "bg-transparent hover:bg-support-light-250"
      }`
      label.innerHTML = `
        <div data-radio class="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
          isChecked ? "border-core-purple-500 bg-core-purple-500" : "border-support-light-400 bg-white"
        }">
          ${isChecked ? `<div class="w-[8px] h-[8px] rounded-full bg-white"></div>` : ""}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold text-support-dark-800 leading-tight">${this.escapeHtml(opt.label)}</div>
          ${opt.description ? `<div class="text-xs text-support-dark-400 mt-0.5 leading-snug">${this.escapeHtml(opt.description)}</div>` : ""}
        </div>
      `
      label.addEventListener("click", (e) => {
        e.preventDefault()
        const currentSelected = this.draftFilters[config.key] || []
        if (currentSelected.includes(opt.value)) {
          delete this.draftFilters[config.key]
        } else {
          this.draftFilters[config.key] = [opt.value]
        }
        updateItems()
        this.renderCategoryList()
        this.updateDraftCount()
      })
      wrapper.appendChild(label)
    }

    container.appendChild(wrapper)
  }

  // --- Enum Select Panel ---

  renderEnumSelectPanel(container, config) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 overflow-y-auto p-4 flex flex-col gap-0.5"

    const rows = []

    const optState = (opt) => {
      const currentSelected = this.draftFilters[config.key] || []
      return currentSelected.includes(opt.value) ? "checked" : "unchecked"
    }

    const checkboxIcon = (state) => {
      if (state === "checked") {
        return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      }
      return ""
    }

    const applyRowStyles = (row, opt) => {
      const state = optState(opt)
      const isHighlighted = state !== "unchecked"
      row.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
        isHighlighted ? "bg-core-purple-200" : "bg-transparent hover:bg-support-light-250"
      }`
      const checkbox = row.querySelector("[data-checkbox]")
      if (checkbox) {
        checkbox.className = `w-[18px] h-[18px] rounded flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
          isHighlighted ? "border-core-purple-500 bg-core-purple-500" : "border-support-light-400 bg-white"
        }`
        checkbox.innerHTML = checkboxIcon(state)
      }
    }

    const updateItems = () => {
      for (const item of rows) {
        applyRowStyles(item.row, item.opt)
      }
    }

    for (const opt of (config.options || [])) {
      const row = document.createElement("label")
      row.dataset.optValue = opt.value
      row.innerHTML = `
        <div data-checkbox></div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold text-support-dark-800 leading-tight">${this.escapeHtml(opt.label)}</div>
          ${opt.description ? `<div class="text-xs text-support-dark-400 mt-0.5 leading-snug">${this.escapeHtml(opt.description)}</div>` : ""}
        </div>
      `
      applyRowStyles(row, opt)

      row.addEventListener("click", (e) => {
        e.preventDefault()
        this.toggleDraftValue(config.key, opt.value)
        updateItems()
        this.renderCategoryList()
        this.updateDraftCount()
      })

      rows.push({ row, opt })
      wrapper.appendChild(row)
    }

    container.appendChild(wrapper)
  }

  // --- Text Search Panel (checkbox list with search) ---

  renderTextSearchPanel(container, config) {
    const selected = this.draftFilters[config.key] || []

    // Search input
    const inputWrap = document.createElement("div")
    inputWrap.className = "px-4 pt-3 pb-2"
    const input = document.createElement("input")
    input.type = "text"
    input.placeholder = config.placeholder || "Search..."
    input.className = "w-full px-3 py-2 border-[1.5px] border-core-purple-300 rounded-md text-[13px] text-support-dark-800 bg-white outline-none box-border focus:border-core-purple-500"
    inputWrap.appendChild(input)
    container.appendChild(inputWrap)

    // Selected items area
    const selectedArea = document.createElement("div")
    selectedArea.className = "px-4"
    container.appendChild(selectedArea)

    const divider = document.createElement("hr")
    divider.className = "mx-4 border-support-light-300"
    container.appendChild(divider)

    // Results area
    const resultsArea = document.createElement("div")
    resultsArea.className = "flex-1 overflow-y-auto px-4 pb-3"
    container.appendChild(resultsArea)

    // Collect all unique values from the table rows
    const allValues = this.collectUniqueValues(config.key)

    const renderSelectedItems = () => {
      const currentSelected = this.draftFilters[config.key] || []
      selectedArea.innerHTML = ""
      divider.classList.toggle("hidden", currentSelected.length === 0)
      for (const val of currentSelected) {
        const row = this.createSearchResultButton(val, true, () => {
          this.toggleDraftValue(config.key, val)
          renderSelectedItems()
          doSearch(input.value)
          this.renderCategoryList()
          this.updateDraftCount()
        })
        selectedArea.appendChild(row)
      }
    }
    renderSelectedItems()

    const doSearch = (query) => {
      resultsArea.innerHTML = ""
      const currentSelected = this.draftFilters[config.key] || []
      const q = query.trim().toLowerCase()
      const filtered = q
        ? allValues.filter(v => v.toLowerCase().includes(q))
        : allValues
      const unselected = filtered.filter(v => !currentSelected.includes(v))

      if (unselected.length === 0 && q) {
        resultsArea.innerHTML = `<div class="text-support-dark-400 text-[13px] py-2">No results found</div>`
        return
      }

      for (const val of unselected) {
        const btn = this.createSearchResultButton(val, false, () => {
          this.toggleDraftValue(config.key, val)
          renderSelectedItems()
          doSearch(input.value)
          this.renderCategoryList()
          this.updateDraftCount()
        })
        resultsArea.appendChild(btn)
      }
    }

    input.addEventListener("input", () => doSearch(input.value))
    requestAnimationFrame(() => input.focus())
    doSearch("")
  }

  createSearchResultButton(result, isSelected, onClick) {
    const btn = document.createElement("button")
    btn.className = `flex items-center gap-2 w-full text-left border-none py-[7px] px-2 text-[13px] cursor-pointer rounded transition-all duration-100 ${
      isSelected ? "bg-core-purple-200 text-core-purple-500 font-semibold" : "bg-transparent text-support-dark-700 font-normal hover:bg-support-light-250"
    }`
    btn.innerHTML = `
      <div class="w-4 h-4 rounded-[3px] border flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
        isSelected ? "border-core-purple-500 bg-core-purple-500" : "border-support-light-400 bg-white"
      }">
        ${isSelected ? `<svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>` : ""}
      </div>
      ${this.escapeHtml(result)}
    `
    btn.addEventListener("click", onClick)
    return btn
  }

  collectUniqueValues(filterKey) {
    const tbody = this.tableBodyTarget
    const rows = tbody.querySelectorAll("tr[data-filter-item-id]")
    const values = new Set()
    const dataAttr = `filter${this.camelize(filterKey)}`
    rows.forEach(row => {
      if (row.dataset.filterIsParent === "true") return
      const val = row.dataset[dataAttr]
      if (val) values.add(val)
    })
    return Array.from(values).sort()
  }

  // --- Date Range Panel ---

  renderDateRangePanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    const parsed = currentValues.length > 0 ? currentValues[0].split("|") : ["", ""]
    let fromVal = parsed[0] || ""
    let toVal = parsed[1] || ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const dateRow = document.createElement("div")
    dateRow.className = "flex gap-3 items-center"

    const fromDiv = document.createElement("div")
    fromDiv.className = "flex-1"
    fromDiv.innerHTML = `<div class="text-[11px] font-semibold text-support-dark-400 mb-1 uppercase tracking-wider">From</div>`
    const fromInput = document.createElement("input")
    fromInput.type = "date"
    fromInput.value = fromVal
    fromInput.className = "w-full px-2.5 py-[7px] border border-support-light-400 rounded-md text-[13px] text-support-dark-800 bg-white outline-none box-border focus:border-core-purple-500"
    fromDiv.appendChild(fromInput)

    const arrow = document.createElement("div")
    arrow.className = "text-support-light-400 text-base mt-[18px]"
    arrow.textContent = "→"

    const toDiv = document.createElement("div")
    toDiv.className = "flex-1"
    toDiv.innerHTML = `<div class="text-[11px] font-semibold text-support-dark-400 mb-1 uppercase tracking-wider">To</div>`
    const toInput = document.createElement("input")
    toInput.type = "date"
    toInput.value = toVal
    toInput.className = "w-full px-2.5 py-[7px] border border-support-light-400 rounded-md text-[13px] text-support-dark-800 bg-white outline-none box-border focus:border-core-purple-500"
    toDiv.appendChild(toInput)

    dateRow.append(fromDiv, arrow, toDiv)
    wrapper.appendChild(dateRow)

    const commitDates = (f, t) => {
      if (!f && !t) {
        delete this.draftFilters[config.key]
      } else {
        this.draftFilters[config.key] = [`${f}|${t}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    fromInput.addEventListener("change", () => {
      fromVal = fromInput.value
      commitDates(fromVal, toVal)
    })
    toInput.addEventListener("change", () => {
      toVal = toInput.value
      commitDates(fromVal, toVal)
    })

    if (fromVal || toVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear dates"
      clearBtn.addEventListener("click", () => {
        fromInput.value = ""
        toInput.value = ""
        fromVal = ""
        toVal = ""
        commitDates("", "")
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // --- Currency Range Panel ---

  renderCurrencyRangePanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    const parsed = currentValues.length > 0 ? currentValues[0].split("|") : ["", "", ""]
    let exactVal = parsed[0] || ""
    let minVal = parsed[1] || ""
    let maxVal = parsed[2] || ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const commitAmounts = () => {
      if (!exactVal && !minVal && !maxVal) {
        delete this.draftFilters[config.key]
      } else {
        this.draftFilters[config.key] = [`${exactVal}|${minVal}|${maxVal}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    const createAmountInput = (labelText, value, onChange) => {
      const row = document.createElement("div")
      row.innerHTML = `<div class="text-[11px] font-semibold text-support-dark-400 mb-1 uppercase tracking-wider">${labelText}</div>`
      const inputWrap = document.createElement("div")
      inputWrap.className = "relative"
      const input = document.createElement("input")
      input.type = "text"
      input.inputMode = "decimal"
      input.value = value
      input.placeholder = "0.00"
      input.className = "w-full currency"
      input.dataset.controller = "format-number"
      const prefix = document.createElement("div")
      prefix.className = "absolute top-0 left-0 z-10 text-support-dark-700 h-full py-2 px-4"
      prefix.textContent = "$"
      input.addEventListener("input", () => {
        const raw = input.value.replace(/[^0-9.]/g, "")
        onChange(raw)
        commitAmounts()
      })
      inputWrap.append(input, prefix)
      row.appendChild(inputWrap)
      return row
    }

    wrapper.appendChild(createAmountInput("Specific amount", exactVal, (v) => { exactVal = v }))

    const divider = document.createElement("div")
    divider.className = "flex items-center gap-2"
    divider.innerHTML = `<hr class="flex-1 border-support-light-300"><span class="text-[11px] font-semibold text-support-dark-400 uppercase">or</span><hr class="flex-1 border-support-light-300">`
    wrapper.appendChild(divider)

    wrapper.appendChild(createAmountInput("At least", minVal, (v) => { minVal = v }))
    wrapper.appendChild(createAmountInput("No more than", maxVal, (v) => { maxVal = v }))

    if (exactVal || minVal || maxVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear amounts"
      clearBtn.addEventListener("click", () => {
        exactVal = ""
        minVal = ""
        maxVal = ""
        commitAmounts()
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // --- Draft helpers ---

  toggleDraftValue(key, value) {
    const current = this.draftFilters[key] || []
    if (current.includes(value)) {
      const next = current.filter(v => v !== value)
      if (next.length === 0) {
        delete this.draftFilters[key]
      } else {
        this.draftFilters[key] = next
      }
    } else {
      this.draftFilters[key] = [...current, value]
    }
  }

  updateDraftCount() {
    const total = Object.values(this.draftFilters).reduce((sum, arr) => sum + arr.length, 0)
    this.draftCountTarget.textContent = total > 0
      ? `${total} filter${total !== 1 ? "s" : ""} selected`
      : "No filters selected"
  }

  // --- Formatting helpers ---

  formatDateRange(val) {
    if (!val) return ""
    const [from, to] = val.split("|")
    const fmt = (d) => {
      if (!d) return ""
      const dt = new Date(d + "T00:00:00")
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    }
    if (from && to) return `${fmt(from)} – ${fmt(to)}`
    if (from) return `After ${fmt(from)}`
    if (to) return `Before ${fmt(to)}`
    return ""
  }

  formatCurrencyRange(val) {
    if (!val) return ""
    const [exact, min, max] = val.split("|")
    const fmt = (v) => `$${parseFloat(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    if (exact) return fmt(exact)
    if (min && max) return `${fmt(min)} – ${fmt(max)}`
    if (min) return `≥ ${fmt(min)}`
    if (max) return `≤ ${fmt(max)}`
    return ""
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
